import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "space-y-2 text-left" }
const _hoisted_2 = { class: "flex flex-wrap items-center justify-between gap-3" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = {
  key: 0,
  class: "text-sm text-red-600"
}
const _hoisted_5 = {
  key: 1,
  class: "py-6 text-center text-gray-500"
}
const _hoisted_6 = {
  key: 0,
  role: "status",
  class: "text-sm text-gray-500"
}
const _hoisted_7 = {
  key: 1,
  role: "alert",
  class: "flex flex-wrap items-center gap-3 rounded-lg bg-red-50 p-3 text-sm text-red-600"
}
const _hoisted_8 = ["disabled", "onClick"]
const _hoisted_9 = { class: "flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2" }
const _hoisted_10 = { class: "flex items-center gap-2" }
const _hoisted_11 = { class: "text-sm font-semibold text-gray-800" }
const _hoisted_12 = { class: "flex flex-wrap items-center gap-2 text-xs" }
const _hoisted_13 = ["href"]
const _hoisted_14 = ["href"]
const _hoisted_15 = {
  key: 2,
  class: "text-gray-500"
}
const _hoisted_16 = { class: "grid grid-cols-1 gap-x-4 gap-y-1 text-sm leading-6 md:grid-cols-2" }
const _hoisted_17 = { class: "w-20 flex-shrink-0 font-normal text-gray-500" }
const _hoisted_18 = { class: "m-0 min-w-0 flex-1 break-all text-gray-800" }

import { computed, onBeforeUnmount, reactive, watch } from 'vue';
  import dayjs from 'dayjs';
  import { getOrderInvoiceList, getSaleInvoiceList } from '@/service/orderInvoice';
  import type { OrderInvoiceInfoModel, SaleInvoiceInfoModel } from '@/model/orderInvoice';
  import { dateTimeFormat } from '@/utils/utils';

  
export default /*@__PURE__*/_defineComponent({
  __name: 'OrderInvoiceDetails',
  props: {
    orderNo: {},
    shopId: {},
    active: { type: Boolean }
  },
  setup(__props: any) {

  const props = __props;

  const sourceNames = ['order', 'sale'] as const;
  const sourceLabels = { order: '订单发票', sale: '销售发票' };
  const createSourceState = () => {
    const records: (OrderInvoiceInfoModel | SaleInvoiceInfoModel)[] = [];
    return { records, loading: false, loaded: false, error: '', requestId: 0 };
  };
  const sources = reactive({ order: createSourceState(), sale: createSourceState() });
  const visibleSources = computed(() =>
    sourceNames.filter((source) => {
      const state = sources[source];
      return state.loading || state.error || state.records.length > 0;
    }),
  );

  const invoiceStatusText = (status?: number | null) => {
    if (status === 1) return '待开票';
    if (status === 10) return '已开票';
    return '未知状态';
  };

  const invoiceFields = (invoice: OrderInvoiceInfoModel | SaleInvoiceInfoModel) => {
    const fields = [
      { label: '发票号', value: invoice.invoiceCode || '—' },
      { label: '订单号', value: invoice.orderNo || '—' },
      { label: '发票抬头', value: invoice.title || '—' },
      { label: '税号', value: invoice.taxNo || '—' },
      {
        label: '开票金额',
        value: invoice.invoiceAmount == null ? '—' : `¥${invoice.invoiceAmount.toFixed(2)}`,
      },
      { label: '接收邮箱', value: invoice.emailAddress || '—' },
      { label: '手机号', value: invoice.phone || '—' },
      {
        label: '创建时间',
        value: invoice.createTime ? dateTimeFormat(dayjs(invoice.createTime)) : '—',
      },
    ];
    if ('orderInvoiceId' in invoice) {
      fields.push(
        {
          label: '发票类型',
          value:
            invoice.invoiceTypeName ||
            (invoice.invoiceType === 1 ? '个人' : invoice.invoiceType === 2 ? '单位' : '—'),
        },
        { label: '发票内容', value: invoice.content || '—' },
        { label: '开户银行', value: invoice.bankName || '—' },
        { label: '银行账户', value: invoice.bankAccount || '—' },
        { label: '单位地址', value: invoice.companyAddress || '—' },
        { label: '单位电话', value: invoice.companyPhone || '—' },
      );
    }
    return fields;
  };

  const safeInvoiceUrl = (value?: string | null): string => {
    if (!value?.trim()) return '';
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch {
      return '';
    }
  };

  const loadSource = async (source: (typeof sourceNames)[number]) => {
    const state = sources[source];
    if (state.loading || !props.orderNo) return;
    const requestId = ++state.requestId;
    const { orderNo, shopId } = props;
    const query = source === 'order' ? getOrderInvoiceList : getSaleInvoiceList;
    state.loading = true;
    state.error = '';
    state.records = [];
    try {
      const records: (OrderInvoiceInfoModel | SaleInvoiceInfoModel)[] = [];
      const pageSize = 100;
      let pageNum = 1;
      while (requestId === state.requestId) {
        const response = await query({ pageNum, pageSize, data: { orderNo, shopId } });
        if (requestId !== state.requestId) return;
        if (response.code !== 200 || !response.result) {
          throw new Error(response.msg || '请稍后重试');
        }
        const page = response.result;
        records.push(...(page.list || []));
        if (!page.list?.length || records.length >= page.total) break;
        pageNum += 1;
      }
      state.records = records;
    } catch (error: unknown) {
      if (requestId !== state.requestId) return;
      console.error(`${sourceLabels[source]}查询失败:`, error);
      state.error = error instanceof Error ? error.message : '请稍后重试';
    } finally {
      if (requestId === state.requestId) {
        state.loading = false;
        state.loaded = true;
      }
    }
  };

  const refreshInvoices = () => {
    void loadSource('order');
    void loadSource('sale');
  };

  const resetSources = () => {
    for (const source of sourceNames) {
      const state = sources[source];
      state.requestId += 1;
      state.records = [];
      state.loading = false;
      state.loaded = false;
      state.error = '';
    }
  };

  watch(
    () => [props.orderNo, props.shopId] as const,
    () => {
      resetSources();
      if (props.active) refreshInvoices();
    },
  );
  watch(
    () => props.active,
    (active) => {
      if (active && !sources.order.loaded && !sources.sale.loaded) refreshInvoices();
    },
    { immediate: true },
  );
  onBeforeUnmount(resetSources);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[1] || (_cache[1] = _createElementVNode("h4", { class: "font-semibold text-gray-800" }, "开票信息", -1)),
      _createElementVNode("button", {
        type: "button",
        class: "text-sm text-blue-600 disabled:opacity-50",
        disabled: sources.order.loading || sources.sale.loading,
        onClick: refreshInvoices
      }, _cache[0] || (_cache[0] = [
        _createElementVNode("i", { class: "fas fa-sync-alt mr-1" }, null, -1),
        _createTextVNode("刷新 ", -1)
      ]), 8, _hoisted_3)
    ]),
    (!_ctx.orderNo)
      ? (_openBlock(), _createElementBlock("p", _hoisted_4, "订单号缺失，无法查询开票信息"))
      : (
        sources.order.loaded &&
        sources.sale.loaded &&
        !sources.order.error &&
        !sources.sale.error &&
        !sources.order.records.length &&
        !sources.sale.records.length
      )
        ? (_openBlock(), _createElementBlock("p", _hoisted_5, " 暂无开票信息 "))
        : _createCommentVNode("", true),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleSources.value, (source) => {
      return (_openBlock(), _createElementBlock("section", {
        key: source,
        class: "space-y-2"
      }, [
        (sources[source].loading)
          ? (_openBlock(), _createElementBlock("p", _hoisted_6, [
              _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-spinner fa-spin mr-1" }, null, -1)),
              _createTextVNode("正在查询" + _toDisplayString(sourceLabels[source]) + "… ", 1)
            ]))
          : _createCommentVNode("", true),
        (sources[source].error)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("span", null, _toDisplayString(sourceLabels[source]) + "查询失败：" + _toDisplayString(sources[source].error), 1),
              _createElementVNode("button", {
                type: "button",
                class: "text-blue-600 disabled:opacity-50",
                disabled: sources[source].loading,
                onClick: ($event: any) => (loadSource(source))
              }, " 重试 ", 8, _hoisted_8)
            ]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sources[source].records, (invoice) => {
          return (_openBlock(), _createElementBlock("article", {
            key: 'orderInvoiceId' in invoice ? invoice.orderInvoiceId : invoice.saleInvoiceId,
            class: "min-w-0 space-y-2 rounded-lg border border-gray-200 p-3"
          }, [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("span", _hoisted_11, _toDisplayString(sourceLabels[source]), 1),
                _createElementVNode("span", {
                  class: _normalizeClass(["text-xs font-medium", invoice.invoiceStatus === 10 ? 'text-green-600' : 'text-gray-600'])
                }, _toDisplayString(invoiceStatusText(invoice.invoiceStatus)), 3)
              ]),
              _createElementVNode("div", _hoisted_12, [
                (safeInvoiceUrl(invoice.invoiceUrl))
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 0,
                      href: safeInvoiceUrl(invoice.invoiceUrl),
                      target: "_blank",
                      rel: "noopener noreferrer",
                      class: "inline-flex items-center rounded border border-gray-300 px-3 py-1 text-gray-600 hover:bg-gray-50"
                    }, _cache[3] || (_cache[3] = [
                      _createElementVNode("i", { class: "fas fa-eye mr-1" }, null, -1),
                      _createTextVNode("预览发票 ", -1)
                    ]), 8, _hoisted_13))
                  : _createCommentVNode("", true),
                (safeInvoiceUrl(invoice.invoiceUrlDownload))
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 1,
                      href: safeInvoiceUrl(invoice.invoiceUrlDownload),
                      download: "",
                      target: "_blank",
                      rel: "noopener noreferrer",
                      class: "inline-flex items-center rounded border border-blue-500 bg-blue-500 px-3 py-1 text-white hover:bg-blue-600"
                    }, _cache[4] || (_cache[4] = [
                      _createElementVNode("i", { class: "fas fa-download mr-1" }, null, -1),
                      _createTextVNode("下载发票 ", -1)
                    ]), 8, _hoisted_14))
                  : (_openBlock(), _createElementBlock("span", _hoisted_15, "暂无下载地址"))
              ])
            ]),
            _createElementVNode("dl", _hoisted_16, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(invoiceFields(invoice), (field) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: field.label,
                  class: "flex min-w-0 items-start"
                }, [
                  _createElementVNode("dt", _hoisted_17, _toDisplayString(field.label) + "：", 1),
                  _createElementVNode("dd", _hoisted_18, _toDisplayString(field.value), 1)
                ]))
              }), 128))
            ])
          ]))
        }), 128))
      ]))
    }), 128))
  ]))
}
}

})